import { useEffect, useMemo, useState, type ChangeEvent } from 'react'
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react'
import { CircleMarker, MapContainer, TileLayer, Tooltip } from 'react-leaflet'
import { useQuery } from '@tanstack/react-query'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import {
  feLocationService,
  type FeDeviceStatus,
  type FeLocationPing,
} from '@/lib/services/feLocationService'

const DEFAULT_CENTER: [number, number] = [14.5995, 120.9842]

const STATUS_COLORS: Record<FeDeviceStatus, string> = {
  online: 'green',
  idle: 'yellow',
  offline: 'gray',
}

function formatTimestamp(iso: string): string {
  if (!iso) return '–'
  return new Date(iso).toLocaleString('en-PH', {
    timeZone: 'Asia/Manila',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

function statusBadge(status?: FeDeviceStatus | null) {
  const value = status ?? 'offline'
  return (
    <Badge colorScheme={STATUS_COLORS[value]} textTransform="capitalize">
      {value}
    </Badge>
  )
}

function mapCenter(pings: FeLocationPing[]): [number, number] {
  const valid = pings.filter((ping) => Number.isFinite(ping.latitude) && Number.isFinite(ping.longitude))
  if (valid.length === 0) return DEFAULT_CENTER
  const lat = valid.reduce((sum, ping) => sum + ping.latitude, 0) / valid.length
  const lng = valid.reduce((sum, ping) => sum + ping.longitude, 0) / valid.length
  return [lat, lng]
}

export default function FELiveMapPage() {
  const colors = useThemeColors()
  const { showError } = useToastNotification()
  const [feUserId, setFeUserId] = useState('')
  const [onlineOnly, setOnlineOnly] = useState(false)
  const [sinceMinutes, setSinceMinutes] = useState('60')
  const [appliedFilters, setAppliedFilters] = useState({
    fe_user_id: undefined as number | undefined,
    online_only: false,
    since_minutes: 60,
  })

  const { data: pings = [], isLoading, isFetching, error } = useQuery({
    queryKey: ['fe-location-pings', appliedFilters],
    queryFn: () => feLocationService.listRecentPings(appliedFilters),
  })

  useEffect(() => {
    if (error) {
      showError(error as Error)
    }
  }, [error, showError])

  const center = useMemo(() => mapCenter(pings), [pings])
  const mapPins = useMemo(
    () => pings.filter((ping) => Number.isFinite(ping.latitude) && Number.isFinite(ping.longitude)),
    [pings],
  )

  const applyFilters = () => {
    setAppliedFilters({
      fe_user_id: feUserId ? Number(feUserId) : undefined,
      online_only: onlineOnly,
      since_minutes: sinceMinutes ? Number(sinceMinutes) : 60,
    })
  }

  return (
    <Box p={4}>
      <Card bg={colors.cardBg} borderColor={colors.border}>
        <CardHeader pb={2}>
          <Heading size="md" color={colors.heading}>
            FE Live Map
          </Heading>
          <Text fontSize="sm" color={colors.textMuted} mt={1}>
            Field engineer locations. A recent ping replaces the profile coordinate and sets device status.
          </Text>
        </CardHeader>
        <CardBody>
          <VStack align="stretch" spacing={4}>
            <HStack align="end" flexWrap="wrap" spacing={3}>
              <FormControl maxW="160px">
                <FormLabel fontSize="sm">FE User ID</FormLabel>
                <Input
                  size="sm"
                  placeholder="Optional"
                  value={feUserId}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setFeUserId(e.target.value.replace(/\D/g, ''))
                  }
                />
              </FormControl>
              <FormControl maxW="160px">
                <FormLabel fontSize="sm">Since (minutes)</FormLabel>
                <Input
                  size="sm"
                  type="number"
                  min={5}
                  value={sinceMinutes}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setSinceMinutes(e.target.value)}
                />
              </FormControl>
              <FormControl maxW="160px">
                <FormLabel fontSize="sm">Online only</FormLabel>
                <Checkbox
                  mt={2}
                  isChecked={onlineOnly}
                  onChange={(e) => setOnlineOnly(e.target.checked)}
                >
                  Show online
                </Checkbox>
              </FormControl>
              <Button size="sm" colorScheme="blue" onClick={applyFilters} alignSelf="end">
                Apply
              </Button>
            </HStack>

            {isLoading || isFetching ? (
              <HStack justify="center" py={10}>
                <Spinner />
                <Text color={colors.textMuted}>Loading location pings…</Text>
              </HStack>
            ) : (
              <>
                <Box
                  h="320px"
                  borderWidth="1px"
                  borderColor={colors.border}
                  borderRadius="md"
                  overflow="hidden"
                  className="store-map-container"
                >
                  {mapPins.length === 0 ? (
                    <HStack h="100%" justify="center">
                      <Text color={colors.textMuted} fontSize="sm">
                        No mappable pings in the selected window.
                      </Text>
                    </HStack>
                  ) : (
                    <MapContainer center={center} zoom={11} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      {mapPins.map((ping) => (
                        <CircleMarker
                          key={ping.id}
                          center={[ping.latitude, ping.longitude]}
                          radius={8}
                          pathOptions={{
                            color: STATUS_COLORS[ping.device_status ?? 'offline'],
                            fillColor: STATUS_COLORS[ping.device_status ?? 'offline'],
                            fillOpacity: 0.85,
                          }}
                        >
                          <Tooltip direction="top" offset={[0, -4]}>
                            {ping.fe_name ?? `FE #${ping.fe_user_id}`}
                          </Tooltip>
                        </CircleMarker>
                      ))}
                    </MapContainer>
                  )}
                </Box>

                <Box overflowX="auto">
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Field Engineer</Th>
                        <Th>Device</Th>
                        <Th>Last ping</Th>
                        <Th>Coordinates</Th>
                        <Th>Linked task</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {pings.length === 0 ? (
                        <Tr>
                          <Td colSpan={5}>
                            <Text color={colors.textMuted} py={4} textAlign="center">
                              No field engineer locations found.
                            </Text>
                          </Td>
                        </Tr>
                      ) : (
                        pings.map((ping) => (
                          <Tr key={ping.id}>
                            <Td>{ping.fe_name ?? `FE #${ping.fe_user_id}`}</Td>
                            <Td>{statusBadge(ping.device_status)}</Td>
                            <Td whiteSpace="nowrap">{formatTimestamp(ping.recorded_at)}</Td>
                            <Td whiteSpace="nowrap">
                              {ping.latitude.toFixed(5)}, {ping.longitude.toFixed(5)}
                            </Td>
                            <Td>{ping.task_number ?? (ping.child_ticket_id ? `#${ping.child_ticket_id}` : '–')}</Td>
                          </Tr>
                        ))
                      )}
                    </Tbody>
                  </Table>
                </Box>
              </>
            )}
          </VStack>
        </CardBody>
      </Card>
    </Box>
  )
}
